import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { SegmentRules, NewsletterCard, LintFinding } from "@shared/newsletterTypes";

export interface NewsletterListRow {
  id: string;
  name: string;
  description: string | null;
  rules: SegmentRules;
  buyerCount: number;
  contactCount: number;
}

export interface IssueRow {
  id: string;
  name: string;
  subject: string;
  status: "draft" | "scheduled" | "sending" | "paused" | "completed" | "cancelled";
  scheduled_start_at: string | null;
  launched_at: string | null;
  created_at: string;
  sent: number;
  pending: number;
  replied: number;
  interest: number;
}

export interface IssueDetail extends Omit<IssueRow, "sent" | "pending" | "replied" | "interest"> {
  body_html: string;
  mailbox_ids: string[] | null;
  list_ids: string[];
  intro_text: string | null;
  cards: NewsletterCard[];
  frozen_at: string | null;
  /** Non-null = a custom-HTML issue: this letter is the body, and there are no cards. */
  custom_html: string | null;
  recipientCounts: Record<string, number>;
}

export interface DealOption {
  id: string;
  name: string;
  industry: string | null;
  state: string | null;
  preview: NewsletterCard;
}

const json = async (url: string, init?: RequestInit) => {
  const res = await fetch(url, {
    credentials: "include",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "Request failed");
  return res.json();
};

export const useNewsletterLists = () =>
  useQuery<NewsletterListRow[]>({ queryKey: ["/api/newsletter/lists"], queryFn: () => json("/api/newsletter/lists") });

export const useDealPicker = () =>
  useQuery<DealOption[]>({ queryKey: ["/api/newsletter/deal-picker"], queryFn: () => json("/api/newsletter/deal-picker") });

export const useNewsletterIssues = () =>
  useQuery<IssueRow[]>({ queryKey: ["/api/newsletter/issues"], queryFn: () => json("/api/newsletter/issues") });

export const useNewsletterIssue = (id: string | undefined) =>
  useQuery<IssueDetail>({
    queryKey: ["/api/newsletter/issues", id],
    queryFn: () => json(`/api/newsletter/issues/${id}`),
    enabled: !!id,
  });

/** Live membership count while the user edits rules. */
export const useSegmentPreview = () =>
  useMutation<{ buyerCount: number; contactCount: number; sample: { buyerName: string; contactName: string; email: string }[] }, Error, SegmentRules>({
    mutationFn: (rules) => json("/api/newsletter/lists/preview", { method: "POST", body: JSON.stringify({ rules }) }),
  });

export function useSaveList() {
  const qc = useQueryClient();
  // POST /lists and PATCH /lists/:id both `RETURNING *` off newsletter_lists,
  // which does NOT include buyerCount/contactCount — those are computed only
  // by GET /lists (a resolveSegment pass per row). Typing this as the full
  // NewsletterListRow would claim those two fields are populated when they'd
  // actually be undefined; the list query invalidation below is what refreshes
  // the real counts.
  return useMutation<Omit<NewsletterListRow, "buyerCount" | "contactCount">, Error, { id?: string; name: string; description?: string; rules: SegmentRules }>({
    mutationFn: ({ id, ...body }) =>
      json(id ? `/api/newsletter/lists/${id}` : "/api/newsletter/lists", {
        method: id ? "PATCH" : "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/newsletter/lists"] }),
  });
}

export function useDeleteList() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (id) => json(`/api/newsletter/lists/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/newsletter/lists"] }),
  });
}

// POST /issues, PATCH /issues/:id, and POST /issues/:id/refresh all respond
// with the bare loadIssue() row. recipientCounts is merged in exactly one
// place server-side — GET /issues/:id — so none of the three mutations below
// can be typed as the full IssueDetail without lying about a field that
// resolves undefined every time.
type IssueDetailNoRecipientCounts = Omit<IssueDetail, "recipientCounts">;

/**
 * Persist a client's deal code name.
 *
 * The card carries the name for THIS issue; this makes it the client's name
 * from now on, so the next issue pre-fills it. Writes companies.project_name
 * only, never the Projects module.
 */
export function useSetProjectName() {
  const qc = useQueryClient();
  return useMutation<{ id: string; projectName: string | null }, Error, { clientId: string; projectName: string }>({
    mutationFn: ({ clientId, projectName }) =>
      json(`/api/newsletter/clients/${clientId}/project-name`, {
        method: "PATCH",
        body: JSON.stringify({ projectName }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/newsletter/deal-picker"] }),
  });
}

export function useCreateIssue() {
  const qc = useQueryClient();
  // cards is optional and additive to clientIds, not a replacement for it:
  // the server still decides which cards exist from clientIds (eligibility
  // gated), and only overlays this array's prose onto a card that already
  // exists. Without sending it, any hand-edit made before the very first
  // save (when there's no issue id yet to PATCH against) was silently
  // discarded and replaced with fresh auto-fill.
  return useMutation<IssueDetailNoRecipientCounts, Error, { name: string; subject: string; clientIds: string[]; cards?: NewsletterCard[]; listIds: string[]; intro: string; mailboxIds?: string[]; customHtml?: string }>({
    mutationFn: (body) => json("/api/newsletter/issues", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/newsletter/issues"] }),
  });
}

export function useUpdateIssue(id: string) {
  const qc = useQueryClient();
  return useMutation<IssueDetailNoRecipientCounts, Error, Partial<{ name: string; subject: string; intro: string; cards: NewsletterCard[]; listIds: string[]; mailboxIds: string[]; customHtml: string }>>({
    mutationFn: (body) => json(`/api/newsletter/issues/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    // Both keys: the detail key alone doesn't cascade to the shorter list key
    // (TanStack invalidation matches by prefix, not suffix), and editing name/
    // subject here is exactly the kind of change an already-mounted issue list
    // needs to pick up.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/newsletter/issues", id] });
      qc.invalidateQueries({ queryKey: ["/api/newsletter/issues"] });
    },
  });
}

export function useRefreshIssue(id: string) {
  const qc = useQueryClient();
  return useMutation<IssueDetailNoRecipientCounts, Error, void>({
    mutationFn: () => json(`/api/newsletter/issues/${id}/refresh`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/newsletter/issues", id] });
      qc.invalidateQueries({ queryKey: ["/api/newsletter/issues"] });
    },
  });
}

export const useLintIssue = (id: string) =>
  useMutation<{ findings: LintFinding[] }, Error, void>({
    mutationFn: () => json(`/api/newsletter/issues/${id}/lint`, { method: "POST" }),
  });

export function useBuildRecipients(id: string) {
  const qc = useQueryClient();
  // missingLists (string[] of list ids that no longer resolve — deleted or
  // archived) must be carried here even though the composer UI doesn't land
  // until Task 13: dropping it from the type would make it silently invisible
  // that a rebuild sent to a fraction of the audience. duplicates/noEmail are
  // NOT included: the server never populates them (see the comment on
  // POST /issues/:id/recipients in newsletter-routes.ts) and a report field
  // that always reads zero is worse than an absent one.
  return useMutation<{ added: number; suppressed: string[]; missingLists: string[] }, Error, void>({
    mutationFn: () => json(`/api/newsletter/issues/${id}/recipients`, { method: "POST" }),
    // Both keys: rebuilding recipients changes sent/pending/replied on the
    // list row too, not just this issue's own detail view.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/newsletter/issues", id] });
      qc.invalidateQueries({ queryKey: ["/api/newsletter/issues"] });
    },
  });
}
